import React, { useState, useEffect, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Skeleton } from "primereact/skeleton";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import BPService from "../../services/resource/bpService";
import { ChartCard } from "../../componentes/chart/CardChart";

const bpService = new BPService();

// ---------------------------------------------------------------------------
// Constantes / utilitários
// ---------------------------------------------------------------------------

const ORDEM_MESES = [
  "JANEIRO", "FEVEREIRO", "MARCO", "ABRIL", "MAIO", "JUNHO",
  "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
];

const LABEL_MESES = {
  JANEIRO: "Jan", FEVEREIRO: "Fev", MARCO: "Mar", ABRIL: "Abr",
  MAIO: "Mai", JUNHO: "Jun", JULHO: "Jul", AGOSTO: "Ago",
  SETEMBRO: "Set", OUTUBRO: "Out", NOVEMBRO: "Nov", DEZEMBRO: "Dez",
};

const COLOR_MAP = {
  positivo: "text-success bg-success/10",
  negativo: "text-destructive bg-destructive/10",
  neutro: "text-muted-foreground bg-muted",
};

const TIPO_ALTERACAO_COLOR_MAP = {
  ALTERADO: "text-amber-700 bg-amber-50",
  INCLUIDO: "text-primary bg-primary/10",
  REMOVIDO: "text-destructive bg-destructive/10",
};

const MAX_ITENS_GRAFICO_RANKING = 12;
const ALTURA_MINIMA_GRAFICO_RANKING = 220;
const ALTURA_MAXIMA_GRAFICO_RANKING = 320;
const CLASSE_MULTISELECT =
  "w-full !rounded-lg !border !border-input !shadow-none hover:!border-primary/50 focus-within:!border-primary focus-within:!ring-2 focus-within:!ring-primary/15 [&_.p-multiselect-label]:!py-2 [&_.p-multiselect-label]:!text-sm [&_.p-multiselect-trigger]:!w-10";
const CLASSE_TABELA_COMPACTA =
  "text-xs [&_.p-datatable-thead>tr>th]:!px-2 [&_.p-datatable-thead>tr>th]:!py-2 [&_.p-datatable-thead>tr>th]:!text-[10px] [&_.p-datatable-tbody>tr>td]:!px-2 [&_.p-datatable-tbody>tr>td]:!py-2 [&_.p-paginator]:!px-2 [&_.p-paginator]:!py-2 [&_.p-paginator_.p-paginator-page]:!text-xs";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(value || 0)
  );

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

// Contexto orçamentário: diferença > 0 significa que 2026 gastou mais que o
// orçado em 2024 (estouro de orçamento => ruim/vermelho). Diferença < 0
// significa economia em relação a 2024 (=> bom/verde).
const variacaoClasse = (valor) => {
  if (valor > 0) return COLOR_MAP.negativo;
  if (valor < 0) return COLOR_MAP.positivo;
  return COLOR_MAP.neutro;
};

const variacaoCorHex = (valor) => {
  if (valor > 0) return "#e11d48";
  if (valor < 0) return "#059669";
  return "#94a3b8";
};

const tipoAlteracaoClasse = (tipo) =>
  TIPO_ALTERACAO_COLOR_MAP[tipo] || COLOR_MAP.neutro;

// Identificador único de uma linha de comparativo (combinação filial + CC + conta)
const chaveLinha = (d) =>
  `${d.filialId}-${d.centroCustoCodigo}-${d.contaContabilReduzido}`;

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function KPICard({ label, value, sublabel, tone, icon, accent }) {
  const cor = accent || "#0f172a";
  return (
    <div
      className="relative min-h-32 overflow-hidden rounded-2xl border border-border bg-card p-4 shadow-card flex flex-col justify-between gap-2"
      style={{ borderLeft: `3px solid ${cor}` }}
    >
      {icon && (
        <div
          className="absolute top-0 right-0 flex items-start justify-end rounded-bl-2xl"
          style={{ width: 56, height: 56, background: `${cor}14`, padding: "8px 10px" }}
        >
          <i className={`fa ${icon} text-base`} style={{ color: cor }} />
        </div>
      )}
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className={`text-xl font-bold tabular-nums ${tone || "text-foreground"}`}>
        {value}
      </span>
      {sublabel && <span className="text-xs text-muted-foreground">{sublabel}</span>}
    </div>
  );
}

function KPICardSkeleton() {
  return (
    <div className="min-h-32 rounded-2xl border border-border bg-card p-4 shadow-card flex flex-col justify-between gap-3">
      <Skeleton width="60%" height="0.7rem" className="!bg-muted !rounded-lg" />
      <Skeleton width="80%" height="1.75rem" className="!bg-muted !rounded-lg" />
    </div>
  );
}

function HintClicavel() {
  return (
    <span className="ml-1 text-[10px] text-muted-foreground font-normal normal-case tracking-normal">
      (clique para filtrar)
    </span>
  );
}

function SectionDivider({ text }) {
  return (
    <div className="flex items-center gap-3 my-2">
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {text}
      </span>
      <div className="h-px bg-border flex-1" />
    </div>
  );
}

// Grid de meses usado tanto na expansão de linha quanto em outros pontos
function MesesGrid({ meses }) {
  const ordenados = [...meses].sort(
    (a, b) => ORDEM_MESES.indexOf(a.mes) - ORDEM_MESES.indexOf(b.mes)
  );
  return (
    <div className="bg-muted rounded-xl p-3">
      <DataTable value={ordenados} className={CLASSE_TABELA_COMPACTA} size="small">
        <Column
          field="mes"
          header="Mês"
          body={(row) => (
            <span className="text-xs text-foreground">
              {LABEL_MESES[row.mes] || row.mes}
            </span>
          )}
        />
        <Column
          field="valor2024"
          header="Valor 2026.1"
          body={(row) => (
            <span className="text-xs text-foreground">
              {formatCurrency(row.valor2024)}
            </span>
          )}
        />
        <Column
          field="valor2026"
          header="Valor 2026"
          body={(row) => (
            <span className="text-xs text-foreground">
              {formatCurrency(row.valor2026)}
            </span>
          )}
        />
        <Column
          field="diferenca"
          header="Diferença"
          body={(row) => (
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-lg ${variacaoClasse(
                row.diferenca
              )}`}
            >
              {formatCurrency(row.diferenca)}
            </span>
          )}
        />
        <Column
          field="variacaoPercentual"
          header="Variação %"
          body={(row) => (
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-lg ${variacaoClasse(
                row.variacaoPercentual
              )}`}
            >
              {formatPercent(row.variacaoPercentual)}
            </span>
          )}
        />
      </DataTable>
    </div>
  );
}

// Tabela usada na visão "tabela" de cada ChartCard, com os mesmos dados
// agregados exibidos no gráfico correspondente
function TabelaResumoGrafico({ linhas }) {
  return (
    <DataTable
      value={linhas}
      className={CLASSE_TABELA_COMPACTA}
      size="small"
      scrollable
      scrollHeight="300px"
      emptyMessage="Sem dados para exibir."
      rowClassName="border-b"
    >
      <Column field="label" header="Item" />
      <Column
        field="valor2024"
        header="2026.1"
        body={(row) => (
          <span className="text-xs text-foreground">
            {formatCurrency(row.valor2024)}
          </span>
        )}
        sortable
      />
      <Column
        field="valor2026"
        header="2026.2"
        body={(row) => (
          <span className="text-xs text-foreground">
            {formatCurrency(row.valor2026)}
          </span>
        )}
        sortable
      />
      <Column
        field="diferenca"
        header="Diferença"
        body={(row) => (
          <span
            className={`text-xs font-semibold px-2 py-1 rounded-lg ${variacaoClasse(
              row.diferenca
            )}`}
          >
            {formatCurrency(row.diferenca)}
          </span>
        )}
        sortable
      />
    </DataTable>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function DashboardComparativoBP() {
  const [dados, setDados] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);

  const [filialSel, setFilialSel] = useState([]);
  const [centroCustoSel, setCentroCustoSel] = useState([]);
  const [contaSel, setContaSel] = useState([]);
  const [tipoAlteracaoSel, setTipoAlteracaoSel] = useState(['ALTERADO']);
  const [tipoSel, setTipoSel] = useState([]);
  const [responsavelSel, setResponsavelSel] = useState([]);
  const [mesSel, setMesSel] = useState([]);

  const [viewMensal, setViewMensal] = useState("chart");
  const [viewCentroCusto, setViewCentroCusto] = useState("chart");
  const [viewConta, setViewConta] = useState("chart");
  const [viewTipo, setViewTipo] = useState("chart");
  const [viewResponsavel, setViewResponsavel] = useState("chart");

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    bpService
      .comparativo()
      .then((resp) => {
        if (ativo) setDados(resp.data || resp || []);
      })
      .catch((err) => {
        if (ativo) setErro("Não foi possível carregar o comparativo de BP.");
        console.error(err);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Opções de filtro derivadas dos dados
  const opcoesFilial = useMemo(
    () =>
      [...new Map(dados.map((d) => [d.filialId, d.filial])).entries()].map(
        ([id, nome]) => ({ label: nome, value: id })
      ),
    [dados]
  );

  const opcoesCentroCusto = useMemo(
    () =>
      [
        ...new Map(
          dados.map((d) => [d.centroCustoCodigo, d.centroCusto])
        ).entries(),
      ].map(([codigo, nome]) => ({ label: `${codigo} - ${nome}`, value: codigo })),
    [dados]
  );

  const opcoesConta = useMemo(
    () =>
      [
        ...new Map(
          dados.map((d) => [d.contaContabilReduzido, d.contaContabil?.trim()])
        ).entries(),
      ].map(([codigo, nome]) => ({ label: `${codigo} - ${nome}`, value: codigo })),
    [dados]
  );

  const opcoesTipoAlteracao = useMemo(
    () =>
      [...new Set(dados.map((d) => d.tipoAlteracao))].map((tipo) => ({
        label: tipo,
        value: tipo,
      })),
    [dados]
  );

  const opcoesTipo = useMemo(
    () =>
      [...new Set(dados.map((d) => d.tipo).filter(Boolean))].map((tipo) => ({
        label: tipo,
        value: tipo,
      })),
    [dados]
  );

  const opcoesResponsavel = useMemo(
    () =>
      [...new Set(dados.map((d) => d.responsavel).filter(Boolean))].map(
        (resp) => ({ label: resp, value: resp })
      ),
    [dados]
  );

  const opcoesMes = useMemo(() => {
    const mesesPresentes = new Set();
    dados.forEach((d) => (d.mesesAlterados || []).forEach((m) => mesesPresentes.add(m.mes)));
    return ORDEM_MESES.filter((m) => mesesPresentes.has(m)).map((m) => ({
      label: LABEL_MESES[m],
      value: m,
    }));
  }, [dados]);

  // Linhas filtradas (nível filial + CC + conta), com os meses também filtrados quando aplicável
  const dadosFiltrados = useMemo(() => {
    return dados
      .filter((d) => {
        if (filialSel.length && !filialSel.includes(d.filialId)) return false;
        if (
          centroCustoSel.length &&
          !centroCustoSel.includes(d.centroCustoCodigo)
        )
          return false;
        if (contaSel.length && !contaSel.includes(d.contaContabilReduzido))
          return false;
        if (
          tipoAlteracaoSel.length &&
          !tipoAlteracaoSel.includes(d.tipoAlteracao)
        )
          return false;
        if (tipoSel.length && !tipoSel.includes(d.tipo)) return false;
        if (responsavelSel.length && !responsavelSel.includes(d.responsavel))
          return false;
        if (
          mesSel.length &&
          !(d.mesesAlterados || []).some((m) => mesSel.includes(m.mes))
        )
          return false;
        return true;
      })
      .map((d) => ({
        ...d,
        mesesAlterados: mesSel.length
          ? (d.mesesAlterados || []).filter((m) => mesSel.includes(m.mes))
          : d.mesesAlterados || [],
      }));
  }, [
    dados,
    filialSel,
    centroCustoSel,
    contaSel,
    tipoAlteracaoSel,
    tipoSel,
    responsavelSel,
    mesSel,
  ]);

  // KPIs (nível totais)
  const kpis = useMemo(() => {
    const total2024 = dadosFiltrados.reduce(
      (acc, d) => acc + Number(d.valorTotal2024 || 0),
      0
    );
    const total2026 = dadosFiltrados.reduce(
      (acc, d) => acc + Number(d.valorTotal2026 || 0),
      0
    );
    const diferenca = total2026 - total2024;
    const variacao = total2024 !== 0 ? (diferenca / total2024) * 100 : 0;
    return {
      total2024,
      total2026,
      diferenca,
      variacao,
      qtdAlteracoes: dadosFiltrados.filter((d) => d.tipoAlteracao === "ALTERADO")
        .length,
    };
  }, [dadosFiltrados]);

  // Série mensal agregada (soma de todas as linhas, mês a mês)
  const serieMensal = useMemo(() => {
    const mapa = new Map();
    dadosFiltrados.forEach((d) => {
      (d.mesesAlterados || []).forEach((m) => {
        const atual = mapa.get(m.mes) || { valor2024: 0, valor2026: 0 };
        atual.valor2024 += Number(m.valor2024 || 0);
        atual.valor2026 += Number(m.valor2026 || 0);
        mapa.set(m.mes, atual);
      });
    });
    const mesesOrdenados = ORDEM_MESES.filter((m) => mapa.has(m));
    return {
      meses: mesesOrdenados,
      categorias: mesesOrdenados.map((m) => LABEL_MESES[m]),
      valores2024: mesesOrdenados.map((m) => mapa.get(m).valor2024),
      valores2026: mesesOrdenados.map((m) => mapa.get(m).valor2026),
    };
  }, [dadosFiltrados]);

  // Top centros de custo por diferença total absoluta
  const topCentrosCusto = useMemo(() => {
    const mapa = new Map();
    dadosFiltrados.forEach((d) => {
      const atual = mapa.get(d.centroCustoCodigo) || {
        codigo: d.centroCustoCodigo,
        nome: d.centroCusto,
        total2024: 0,
        total2026: 0,
        diferenca: 0,
      };
      atual.total2024 += Number(d.valorTotal2024 || 0);
      atual.total2026 += Number(d.valorTotal2026 || 0);
      atual.diferenca += Number(d.diferencaTotal || 0);
      mapa.set(d.centroCustoCodigo, atual);
    });
    return [...mapa.values()]
      .sort((a, b) => Math.abs(b.diferenca) - Math.abs(a.diferenca))
  }, [dadosFiltrados]);

  // Top contas contábeis por diferença total absoluta
  const topContas = useMemo(() => {
    const mapa = new Map();
    dadosFiltrados.forEach((d) => {
      const atual = mapa.get(d.contaContabilReduzido) || {
        codigo: d.contaContabilReduzido,
        nome: d.contaContabil?.trim(),
        total2024: 0,
        total2026: 0,
        diferenca: 0,
      };
      atual.total2024 += Number(d.valorTotal2024 || 0);
      atual.total2026 += Number(d.valorTotal2026 || 0);
      atual.diferenca += Number(d.diferencaTotal || 0);
      mapa.set(d.contaContabilReduzido, atual);
    });
    return [...mapa.values()]
      .sort((a, b) => Math.abs(b.diferenca) - Math.abs(a.diferenca))
      .slice(0, MAX_ITENS_GRAFICO_RANKING);
  }, [dadosFiltrados]);

  const topContasTabela = useMemo(() => {
    const mapa = new Map();
    dadosFiltrados.forEach((d) => {
      const atual = mapa.get(d.contaContabilReduzido) || {
        codigo: d.contaContabilReduzido,
        nome: d.contaContabil?.trim(),
        total2024: 0,
        total2026: 0,
        diferenca: 0,
      };
      atual.total2024 += Number(d.valorTotal2024 || 0);
      atual.total2026 += Number(d.valorTotal2026 || 0);
      atual.diferenca += Number(d.diferencaTotal || 0);
      mapa.set(d.contaContabilReduzido, atual);
    });
    return [...mapa.values()]
      .sort((a, b) => Math.abs(b.diferenca) - Math.abs(a.diferenca))
  }, [dadosFiltrados]);

  // Diferença agregada por Tipo (ex: Folha, Frota, Combustível...)
  const topTipos = useMemo(() => {
    const mapa = new Map();
    dadosFiltrados.forEach((d) => {
      const chave = d.tipo || "Sem tipo";
      const atual = mapa.get(chave) || {
        tipo: chave,
        total2024: 0,
        total2026: 0,
        diferenca: 0,
      };
      atual.total2024 += Number(d.valorTotal2024 || 0);
      atual.total2026 += Number(d.valorTotal2026 || 0);
      atual.diferenca += Number(d.diferencaTotal || 0);
      mapa.set(chave, atual);
    });
    return [...mapa.values()].sort(
      (a, b) => Math.abs(b.diferenca) - Math.abs(a.diferenca)
    );
  }, [dadosFiltrados]);

  // Diferença agregada por Responsável
  const topResponsaveis = useMemo(() => {
    const mapa = new Map();
    dadosFiltrados.forEach((d) => {
      const chave = d.responsavel || "Sem responsável";
      const atual = mapa.get(chave) || {
        responsavel: chave,
        total2024: 0,
        total2026: 0,
        diferenca: 0,
      };
      atual.total2024 += Number(d.valorTotal2024 || 0);
      atual.total2026 += Number(d.valorTotal2026 || 0);
      atual.diferenca += Number(d.diferencaTotal || 0);
      mapa.set(chave, atual);
    });
    return [...mapa.values()]
      .sort((a, b) => Math.abs(b.diferenca) - Math.abs(a.diferenca))
      .slice(0, MAX_ITENS_GRAFICO_RANKING);
  }, [dadosFiltrados]);

  // ---------------------------------------------------------------------
  // Templates de coluna (nível linha / totais)
  // ---------------------------------------------------------------------

  const templateFilial = (row) => (
    <span className="text-xs text-foreground">{row.filial}</span>
  );

  const templateCentroCusto = (row) => (
    <div className="flex flex-col">
      <span className="text-xs text-foreground">{row.centroCusto}</span>
      <span className="text-[10px] text-muted-foreground">{row.centroCustoCodigo}</span>
    </div>
  );

  const templateConta = (row) => (
    <div className="flex flex-col">
      <span className="text-xs text-foreground">{row.contaContabil?.trim()}</span>
      <span className="text-[10px] text-muted-foreground">
        {row.contaContabilReduzido}
      </span>
    </div>
  );

  const templateTipo = (row) => (
    <span className="text-xs text-foreground">{row.tipo}</span>
  );

  const templateResponsavel = (row) => (
    <span className="text-xs text-foreground">{row.responsavel}</span>
  );

  const templateTipoAlteracao = (row) => (
    <span
      className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-lg ${tipoAlteracaoClasse(
        row.tipoAlteracao
      )}`}
    >
      {row.tipoAlteracao}
    </span>
  );

  const templateValor = (campo) => (row) => (
    <span className="text-xs text-foreground">{formatCurrency(row[campo])}</span>
  );

  const templateDiferencaTotal = (row) => (
    <span
      className={`text-xs font-semibold px-2 py-1 rounded-lg ${variacaoClasse(
        row.diferencaTotal
      )}`}
    >
      {formatCurrency(row.diferencaTotal)}
    </span>
  );

  const templateVariacaoTotal = (row) => (
    <span
      className={`text-xs font-semibold px-2 py-1 rounded-lg ${variacaoClasse(
        row.variacaoPercentualTotal
      )}`}
    >
      {formatPercent(row.variacaoPercentualTotal)}
    </span>
  );

  const templateQtdMeses = (row) => (
    <span className="text-xs text-muted-foreground">
      {(row.mesesAlterados || []).length} mês(es)
    </span>
  );

  const exportarDetalhamento = () => {
    const linhas = dadosFiltrados.map((row) => ({
      Filial: row.filial,
      "Centro de Custo": row.centroCusto,
      "Cód. Centro de Custo": row.centroCustoCodigo,
      "Conta Contábil": row.contaContabil?.trim(),
      "Cód. Conta Contábil": row.contaContabilReduzido,
      Tipo: row.tipo,
      Responsável: row.responsavel,
      Status: row.tipoAlteracao,
      "Total 2026.1": Number(row.valorTotal2024 || 0),
      "Total 2026.2": Number(row.valorTotal2026 || 0),
      Diferença: Number(row.diferencaTotal || 0),
      "Variação (%)": Number(row.variacaoPercentualTotal || 0),
      Meses: (row.mesesAlterados || []).length,
    }));

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    worksheet["!cols"] = [
      { wch: 22 }, { wch: 32 }, { wch: 18 }, { wch: 38 }, { wch: 18 },
      { wch: 22 }, { wch: 26 }, { wch: 14 }, { wch: 16 }, { wch: 16 },
      { wch: 16 }, { wch: 14 }, { wch: 10 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Detalhamento BP");
    XLSX.writeFile(workbook, "comparativo-bp-detalhamento.xlsx");
  };

  const rowExpansionTemplate = (row) => (
    <div className="p-2">
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        Detalhamento mensal
      </span>
      <div className="mt-2">
        <MesesGrid meses={row.mesesAlterados || []} />
      </div>
    </div>
  );

  // ---------------------------------------------------------------------
  // Opções dos gráficos ApexCharts
  // ---------------------------------------------------------------------

  // Alterna um valor dentro de um array de seleção de filtro (clique no
  // gráfico funciona como atalho para adicionar/remover do filtro correspondente)
  const alternarSelecao = (setter) => (valor) => {
    setter((prev) =>
      prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor]
    );
  };

  const opcoesGraficoMensal = {
    chart: {
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          const mes = serieMensal.meses[config.dataPointIndex];
          if (!mes) return;
          alternarSelecao(setMesSel)(mes);
        },
      },
    },
    xaxis: { categories: serieMensal.categorias },
    colors: ["#94a3b8", "#0f172a"],
    plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
    dataLabels: { enabled: false },
    legend: { position: "top", horizontalAlign: "right" },
    yaxis: {
      labels: {
        formatter: (v) =>
          new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(v),
      },
    },
    tooltip: {
      y: { formatter: (v) => formatCurrency(v) },
    },
    grid: { borderColor: "#e2e8f0" },
    states: { active: { filter: { type: "darken", value: 0.85 } } },
  };

  const serieGraficoMensal = [
    { name: "2026.1", data: serieMensal.valores2024 },
    { name: "2026.2", data: serieMensal.valores2026 },
  ];

  const linhasTabelaMensal = serieMensal.categorias.map((categoria, i) => ({
    label: categoria,
    valor2024: serieMensal.valores2024[i],
    valor2026: serieMensal.valores2026[i],
    diferenca: serieMensal.valores2026[i] - serieMensal.valores2024[i],
  }));

  const opcoesGraficoCentroCusto = {
    chart: {
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          const item = topCentrosCusto[config.dataPointIndex];
          if (!item) return;
          alternarSelecao(setCentroCustoSel)(item.codigo);
        },
      },
    },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
    xaxis: {
      categories: topCentrosCusto.map((c) => `${c.codigo} - ${c.nome}`),
      labels: {
        formatter: (v) =>
          new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(v),
      },
    },
    colors: topCentrosCusto.map((c) => variacaoCorHex(c.diferenca)),
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: "#e2e8f0" },
    states: { active: { filter: { type: "darken", value: 0.85 } } },
  };

  const serieGraficoCentroCusto = [
    { name: "Diferença", data: topCentrosCusto.map((c) => c.diferenca) },
  ];

  const linhasTabelaCentroCusto = topCentrosCusto.map((c) => ({
    label: `${c.codigo} - ${c.nome}`,
    valor2024: c.total2024,
    valor2026: c.total2026,
    diferenca: c.diferenca,
  }));

  const opcoesGraficoConta = {
    chart: {
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          const item = topContas[config.dataPointIndex];
          if (!item) return;
          alternarSelecao(setContaSel)(item.codigo);
        },
      },
    },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
    xaxis: {
      categories: topContas.map((c) => `${c.codigo} - ${c.nome}`),
      labels: {
        formatter: (v) =>
          new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(v),
      },
    },
    colors: topContas.map((c) => variacaoCorHex(c.diferenca)),
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: "#e2e8f0" },
    states: { active: { filter: { type: "darken", value: 0.85 } } },
  };

  const serieGraficoConta = [
    { name: "Diferença", data: topContas.map((c) => c.diferenca) },
  ];

  const linhasTabelaConta = topContasTabela.map((c) => ({
    label: `${c.codigo} - ${c.nome}`,
    valor2024: c.total2024,
    valor2026: c.total2026,
    diferenca: c.diferenca,
  }));

  const opcoesGraficoTipo = {
    chart: {
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          const item = topTipos[config.dataPointIndex];
          if (!item) return;
          alternarSelecao(setTipoSel)(item.tipo);
        },
      },
    },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true } },
    xaxis: {
      categories: topTipos.map((t) => t.tipo),
      labels: {
        formatter: (v) =>
          new Intl.NumberFormat("pt-BR", { notation: "compact" }).format(v),
      },
    },
    colors: topTipos.map((t) => variacaoCorHex(t.diferenca)),
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: "#e2e8f0" },
    states: { active: { filter: { type: "darken", value: 0.85 } } },
  };

  const serieGraficoTipo = [
    { name: "Diferença", data: topTipos.map((t) => t.diferenca) },
  ];

  const linhasTabelaTipo = topTipos.map((t) => ({
    label: t.tipo,
    valor2024: t.total2024,
    valor2026: t.total2026,
    diferenca: t.diferenca,
  }));

  const opcoesGraficoResponsavel = {
    chart: {
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          const item = topResponsaveis[config.dataPointIndex];
          if (!item) return;
          alternarSelecao(setResponsavelSel)(item.responsavel);
        },
      },
    },

    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        distributed: true,
        columnWidth: "55%",
      },
    },

    xaxis: {
      categories: topResponsaveis.map((r) => r.responsavel),
      labels: {
        rotate: -45,
        trim: true,
        hideOverlappingLabels: false,
      },
      axisBorder: {
        show: true,
      },
    },

    yaxis: {
      labels: {
        formatter: (v) =>
          new Intl.NumberFormat("pt-BR", {
            notation: "compact",
          }).format(v),
      },
    },

    colors: topResponsaveis.map((r) => variacaoCorHex(r.diferenca)),

    dataLabels: {
      enabled: false,
    },

    legend: {
      show: false,
    },

    tooltip: {
      y: {
        formatter: (v) => formatCurrency(v),
      },
    },

    grid: {
      borderColor: "#e2e8f0",
    },

    states: {
      active: {
        filter: {
          type: "darken",
          value: 0.85,
        },
      },
    },
  };

  const serieGraficoResponsavel = [
    { name: "Diferença", data: topResponsaveis.map((r) => r.diferenca) },
  ];

  const linhasTabelaResponsavel = topResponsaveis.map((r) => ({
    label: r.responsavel,
    valor2024: r.total2024,
    valor2026: r.total2026,
    diferenca: r.diferenca,
  }));

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-background pb-10">
      {/* Topbar */}
      <div className="sticky top-0 z-10 bg-card border-b border-border shadow-xs">
        <div className="mx-auto px-4 py-3 flex items-center gap-3">
          <div className="font-display w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
            BP
          </div>
          <div className="flex flex-col">
            <span className="font-display text-sm font-bold text-foreground">
              Comparativo BP 2026.1 vs 2026.2
            </span>
            <span className="text-[10px] text-muted-foreground">
              Distribuidora York · Planejamento Orçamentário
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 mt-6 flex flex-col gap-6">
        {erro && (
          <div className="rounded-2xl border border-destructive/20 bg-destructive/10 text-destructive text-sm px-4 py-3">
            {erro}
          </div>
        )}

        {/* Filtros */}
        <div className="rounded-2xl border border-border shadow-card bg-card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Filial
            </span>
            <MultiSelect
              value={filialSel}
              options={opcoesFilial}
              onChange={(e) => setFilialSel(e.value)}
              placeholder="Todas"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Centro de Custo
            </span>
            <MultiSelect
              value={centroCustoSel}
              options={opcoesCentroCusto}
              onChange={(e) => setCentroCustoSel(e.value)}
              placeholder="Todos"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Conta Contábil
            </span>
            <MultiSelect
              value={contaSel}
              options={opcoesConta}
              onChange={(e) => setContaSel(e.value)}
              placeholder="Todas"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Tipo de Alteração
            </span>
            <MultiSelect
              value={tipoAlteracaoSel}
              options={opcoesTipoAlteracao}
              onChange={(e) => setTipoAlteracaoSel(e.value)}
              placeholder="Todos"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Tipo
            </span>
            <MultiSelect
              value={tipoSel}
              options={opcoesTipo}
              onChange={(e) => setTipoSel(e.value)}
              placeholder="Todos"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Responsável
            </span>
            <MultiSelect
              value={responsavelSel}
              options={opcoesResponsavel}
              onChange={(e) => setResponsavelSel(e.value)}
              placeholder="Todos"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Mês
            </span>
            <MultiSelect
              value={mesSel}
              options={opcoesMes}
              onChange={(e) => setMesSel(e.value)}
              placeholder="Todos"
              display="chip"
              className={CLASSE_MULTISELECT}
              disabled={carregando}
            />
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {carregando ? (
            <>
              <KPICardSkeleton />
              <KPICardSkeleton />
              <KPICardSkeleton />
              <KPICardSkeleton />
            </>
          ) : (
            <>
              <KPICard
                label="Total 2026.1"
                value={formatCurrency(kpis.total2024)}
                icon="fa-calendar-alt"
                accent="#0f172a"
              />
              <KPICard
                label="Total 2026.2"
                value={formatCurrency(kpis.total2026)}
                icon="fa-calendar-check"
                accent="#0ea5e9"
              />
              <KPICard
                label="Diferença"
                value={formatCurrency(kpis.diferenca)}
                sublabel={kpis.diferenca <= 0 ? "Economia" : "Estouro de orçamento"}
                icon={kpis.diferenca > 0 ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}
                accent={
                  kpis.diferenca > 0
                    ? "#e11d48"
                    : kpis.diferenca < 0
                      ? "#059669"
                      : "#94a3b8"
                }
                tone={
                  kpis.diferenca > 0
                    ? "text-destructive"
                    : kpis.diferenca < 0
                      ? "text-success"
                      : "text-foreground"
                }
              />
              <KPICard
                label="Variação %"
                value={formatPercent(kpis.variacao)}
                sublabel={`${kpis.qtdAlteracoes} linha(s) alterada(s)`}
                icon="fa-percent"
                accent={
                  kpis.variacao > 0
                    ? "#e11d48"
                    : kpis.variacao < 0
                      ? "#059669"
                      : "#94a3b8"
                }
                tone={
                  kpis.variacao > 0
                    ? "text-destructive"
                    : kpis.variacao < 0
                      ? "text-success"
                      : "text-foreground"
                }
              />
            </>
          )}
        </div>

        {/* Gráficos */}
        <SectionDivider text="Análise Gráfica" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard
            title={<>Comparativo mensal (2026.1 x 2026.2) <HintClicavel /></>}
            view={viewMensal}
            onViewChange={setViewMensal}
            accent="#0f172a"
            tableContent={<TabelaResumoGrafico linhas={linhasTabelaMensal} />}
          >
            {carregando ? (
              <Skeleton height="18rem" className="!bg-muted !rounded-lg" />
            ) : (
              <div className="cursor-pointer">
                <Chart
                  options={opcoesGraficoMensal}
                  series={serieGraficoMensal}
                  type="bar"
                  height={300}
                />
              </div>
            )}
          </ChartCard>
          <ChartCard
            title={<>Maiores diferenças por centro de custo <HintClicavel /></>}
            view={viewCentroCusto}
            onViewChange={setViewCentroCusto}
            accent="#0ea5e9"
            tableContent={<TabelaResumoGrafico linhas={linhasTabelaCentroCusto} />}
          >
            {carregando ? (
              <Skeleton height="18rem" className="!bg-muted !rounded-lg" />
            ) : (
              <div className="cursor-pointer">
                <Chart
                  options={opcoesGraficoCentroCusto}
                  series={serieGraficoCentroCusto}
                  type="bar"
                  height={300}
                />
              </div>
            )}
          </ChartCard>
          <ChartCard
            title={<>Diferença por Tipo <HintClicavel /></>}
            view={viewTipo}
            onViewChange={setViewTipo}
            accent="#f59e0b"
            tableContent={<TabelaResumoGrafico linhas={linhasTabelaTipo} />}
          >
            {carregando ? (
              <Skeleton height="18rem" className="!bg-muted !rounded-lg" />
            ) : (
              <div className="max-h-[250px] overflow-y-auto overflow-x-hidden pr-2 cursor-pointer">
                <Chart
                  options={opcoesGraficoTipo}
                  series={serieGraficoTipo}
                  type="bar"
                  height={Math.max(ALTURA_MINIMA_GRAFICO_RANKING, topTipos.length * 36)}
                />
              </div>
            )}
          </ChartCard>
          <ChartCard
            title={<>Diferença por Responsável <HintClicavel /></>}
            view={viewResponsavel}
            onViewChange={setViewResponsavel}
            accent="#8b5cf6"
            tableContent={<TabelaResumoGrafico linhas={linhasTabelaResponsavel} />}
          >
            {carregando ? (
              <Skeleton height="18rem" className="!bg-muted !rounded-lg" />
            ) : (
              <div className="cursor-pointer">
                <Chart
                  options={opcoesGraficoResponsavel}
                  series={serieGraficoResponsavel}
                  type="bar"
                  height={Math.min(
                    ALTURA_MAXIMA_GRAFICO_RANKING,
                    Math.max(ALTURA_MINIMA_GRAFICO_RANKING, topResponsaveis.length * 36)
                  )}
                />
              </div>
            )}
          </ChartCard>
          <div className="col-span-2">
            <ChartCard
              title={<>Maiores diferenças por Conta Contábil <HintClicavel /></>}
              view={viewConta}
              onViewChange={setViewConta}
              accent="#10b981"
              tableContent={<TabelaResumoGrafico linhas={linhasTabelaConta} />}
            >
              {carregando ? (
                <Skeleton height="18rem" className="!bg-muted !rounded-lg" />
              ) : (
                <div className="cursor-pointer">
                  <Chart
                    options={opcoesGraficoConta}
                    series={serieGraficoConta}
                    type="bar"
                    height={300}
                  />
                </div>
              )}
            </ChartCard>
          </div>
        </div>

        {/* Tabela detalhada */}
        <SectionDivider text="Detalhamento" />
        <div className="rounded-2xl border border-border shadow-card bg-card p-2">
          <div className="flex justify-end px-2 pb-2">
            <button
              type="button"
              onClick={exportarDetalhamento}
              disabled={carregando || dadosFiltrados.length === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-success px-3 py-2 text-xs font-semibold text-success transition-colors duration-150 hover:bg-success/10 disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:opacity-50"
            >
              <i className="fa fa-file-excel" />
              Exportar Excel
            </button>
          </div>
          <DataTable
            value={dadosFiltrados}
            loading={carregando}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
            responsiveLayout="scroll"
            emptyMessage="Nenhum registro encontrado para os filtros selecionados."
            className={CLASSE_TABELA_COMPACTA}
            dataKey={chaveLinha}
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
          >
            <Column className="border-bottom" expander style={{ width: "3rem" }} />
            <Column className="border-bottom" field="filial" header="Filial" body={templateFilial} sortable />
            <Column
              className="border-bottom"
              field="centroCusto"
              header="Centro de Custo"
              body={templateCentroCusto}
              sortable
            />
            <Column
              className="border-bottom"
              field="contaContabil"
              header="Conta Contábil"
              body={templateConta}
              sortable
            />
            <Column className="border-bottom" field="tipo" header="Tipo" body={templateTipo} sortable />
            <Column
              className="border-bottom"
              field="responsavel"
              header="Responsável"
              body={templateResponsavel}
              sortable
            />
            <Column
              className="border-bottom"
              field="tipoAlteracao"
              header="Status"
              body={templateTipoAlteracao}
              sortable
            />
            <Column
              className="border-bottom"
              field="valorTotal2024"
              header="Total 2026.1"
              body={templateValor("valorTotal2024")}
              sortable
            />
            <Column
              className="border-bottom"
              field="valorTotal2026"
              header="Total 2026.2"
              body={templateValor("valorTotal2026")}
              sortable
            />
            <Column
              className="border-bottom text-nowrap"
              field="diferencaTotal"
              header="Diferença"
              body={templateDiferencaTotal}
              sortable
            />
            <Column
              className="border-bottom"
              field="variacaoPercentualTotal"
              header="Variação %"
              body={templateVariacaoTotal}
              sortable
            />
            <Column className="border-bottom" header="Meses" body={templateQtdMeses} style={{ width: "6rem" }} />
          </DataTable>
        </div>
      </div>
    </div>
  );
}
